import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useRouteError } from "react-router-dom";
import {
  RotateCw,
  Home,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import * as Sentry from "@sentry/react";

import { Button } from "@stockline/ui/components/button";
import { FeedbackForm } from "@/components/feedback-form";
import {
  isChunkLoadError,
  markReloadAttempt,
  reloadBudgetAvailable,
} from "@/lib/chunkError";

const VARIANTS = ["dropped", "hiccup", "spilled", "confused"] as const;
const LABEL_COUNT = 5;

function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const location = useLocation();
  const initialPath = useRef(location.pathname);

  // Any navigation away dismisses the error. The error boundary lives inside
  // the layout, so sidebar clicks don't unmount the fallback on their own —
  // we have to clear the boundary state explicitly when the URL changes.
  useEffect(() => {
    if (location.pathname !== initialPath.current) {
      resetError();
    }
  }, [location.pathname, resetError]);

  // Pick a variant + label once per mount — stable across rerenders.
  const variant = useMemo(
    () => VARIANTS[Math.floor(Math.random() * VARIANTS.length)],
    [],
  );
  const labelIndex = useMemo(() => Math.floor(Math.random() * LABEL_COUNT), []);

  const errorMessage =
    error instanceof Error ? error.message : String(error ?? "Unknown error");

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center gap-6 p-8 text-center">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-100 border border-red-200 text-red-700 font-semibold text-sm dark:bg-red-950/40 dark:border-red-900/50 dark:text-red-400">
        <AlertTriangle className="h-4 w-4" />
        {t(
          `errors.errorPage.labels.${labelIndex}`,
          "Oops! Something went wrong.",
        )}
      </div>

      <img
        src="/error.webp"
        alt=""
        aria-hidden="true"
        className="w-full max-w-lg h-auto select-none opacity-90"
        draggable={false}
      />

      <div className="flex flex-col items-center gap-3 max-w-lg">
        <h1 className="text-6xl font-black tracking-tighter text-foreground/70 leading-none">
          500
        </h1>
        <h2 className="text-2xl font-semibold">
          {t(`errors.errorPage.variants.${variant}.heading`)}
        </h2>
        <p className="text-muted-foreground">
          {t(`errors.errorPage.variants.${variant}.subtitle`)}
        </p>
      </div>

      <div className="flex flex-wrap gap-3 justify-center">
        <Button variant="outline" onClick={resetError}>
          <RotateCw className="h-4 w-4 mr-2" />
          {t("errors.errorPage.tryAgain", "Try again")}
        </Button>
        <Button
          variant="outline"
          asChild
          className="border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600/60 hover:text-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
        >
          <Link to="/home" onClick={resetError}>
            <Home className="h-4 w-4 mr-2" />
            {t("errors.errorPage.goHome", "Back to home")}
          </Link>
        </Button>
      </div>

      <FeedbackForm pageKind="error" errorMessage={errorMessage} />

      {errorMessage && (
        <div className="max-w-xl w-full">
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          >
            {showDetails ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            {t("errors.errorPage.details", "Show error details")}
          </button>
          {showDetails && (
            <pre className="mt-2 p-3 rounded bg-muted/60 font-mono text-xs text-left overflow-auto max-h-48 text-muted-foreground">
              {errorMessage}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  // Reset the boundary when the top-level section changes. `Sentry.ErrorBoundary`
  // has no `resetKeys`, so remounting via `key` is how we clear a stuck error
  // state on navigation — the safety net behind the notranslate guard for the
  // Google-Translate `insertBefore` crash (NEXTJS-FRONTEND-3Q/-3R). Keyed by the
  // FIRST path segment (mirrors root-layout's PageTransition key), not the full
  // path, so /recap/:a → /recap/:b doesn't remount the heavy workspace — only a
  // genuine section change (which already remounts via AnimatePresence) does.
  // Within-section recovery stays handled by ErrorFallback's own pathname reset.
  const sectionKey = pathname.split("/")[1] || "root";
  return (
    <Sentry.ErrorBoundary key={sectionKey} fallback={ErrorFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
}

// Minimal screen shown while a stale-deploy chunk failure auto-reloads.
function UpdatingScreen() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <RotateCw className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-muted-foreground">
        {t(
          "errors.errorPage.updating",
          "A new version is available — reloading…",
        )}
      </p>
    </div>
  );
}

/**
 * Error boundary for React Router's data router (`errorElement`).
 *
 * Lazy route chunks (`router.tsx` `page()` → RR `lazy`) fail at the
 * routing layer, not the React render tree — so the in-layout
 * `Sentry.ErrorBoundary` never sees them, and without an `errorElement`
 * RR shows its bare default page. This boundary closes that gap: it
 * reports to Sentry explicitly, auto-recovers from stale-deploy chunk
 * failures (reload once, budget-guarded against loops), and otherwise
 * renders the branded fallback instead of RR's default.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const chunk = isChunkLoadError(error);
  // Read-only budget check during render keeps render pure; the stamp +
  // reload happen in the effect below.
  const willReload = chunk && reloadBudgetAvailable();

  useEffect(() => {
    Sentry.captureException(error, {
      level: chunk ? "warning" : "error",
      tags: { errorBoundary: "route", chunkLoad: chunk },
    });
    if (willReload) {
      markReloadAttempt();
      window.location.reload();
    }
  }, [error, chunk, willReload]);

  if (willReload) return <UpdatingScreen />;

  // Non-chunk route errors, and chunk errors whose reload budget is spent
  // (suspected broken deploy), fall through to the branded 500 page with a
  // manual reload.
  return (
    <ErrorFallback
      error={error}
      componentStack=""
      eventId=""
      resetError={() => window.location.reload()}
    />
  );
}
